<template>
    <div class="institution-management">
        <!-- 引入通用表格组件 -->
        <UniversalDataTable
            :columns="institutionColumns"
            :form-fields="institutionFormFields"
            :table-data="tableData"
            :pagination="pagination"
            :form-rules="institutionFormRules"
            :uploadUrl="uploadImgUrl"
            :enable-selection="true"
            :enable-edit="true"
            :enable-delete="true"
            @search="handleSearch"
            @create="handleCreate"
            @update="handleUpdate"
            @delete="handleDelete"
            @pagination-change="handlePaginationChange"
            @selection-change="handleSelectionChange" />
    </div>
</template>

<script>
// 引入通用表格组件（确保路径正确）
import UniversalDataTable from '@/components/common/commonTable.vue';
import institutionApi from '@/api/institutions.js'; // 替换为实际的机构接口文件
import baseData from '@/api/base.js';
import { sliceImgUrl } from '@/utils/MyUtils.js'

export default {
    name: 'InstitutionManagement',
    components: {
        UniversalDataTable
    },
    data() {
        return {
            // 表格数据（实际从接口获取）
            tableData: [],
            // 分页配置
            pagination: {
                currentPage: 1,
                pageSize: 10,
                total: 0
            },
            //搜索相关
            keyword: '',
            uploadImgUrl: baseData.IMG_BASE_URL
        };
    },
    computed: {
        // 基础分页查询URL
        baseUrl() {
            return `/institutions/page?pageNum=${this.pagination.currentPage}&pageSize=${this.pagination.pageSize}&name=${this.keyword}`
        },
        // 带关键词的搜索URL
        keywordUrl() {
            return (keyword) => `/institutions/search/page?nameKeyword=${keyword}&pageNum=${this.pagination.currentPage}&pageSize=${this.pagination.pageSize}`
        },
        // 表格列配置（对应机构数据字段）
        institutionColumns() {
            return [
                {
                    prop: 'id',
                    label: 'ID',
                    width: 80,
                    align: 'center'
                },
                {
                    prop: 'name',
                    label: '机构名称',
                    width: 200
                },
                {
                    prop: 'type',
                    label: '机构类型',
                    width: 120,
                    align: 'center',
                    // 格式化类型显示（1=企业，2=高校）
                    formatter: (value) => {
                        return value === 1 ? '企业' : value === 2 ? '高校' : '未知';
                    }
                },
                {
                    prop: 'address',
                    label: '地址',
                    width: 250
                },
                {
                    prop: 'phone',
                    label: '联系电话',
                    width: 150,
                    align: 'center'
                },
                {
                    prop: 'introduction',
                    label: '机构介绍',
                    // 超长文本截取显示（hover 显示完整内容）
                    formatter: (value) => {
                        if (!value) return '';
                        return value.length > 30 ? `${value.slice(0, 30)}...` : value;
                    }
                },
                {
                    prop: 'introImage',
                    label: '介绍图片',
                    type: 'image',  // 触发通用组件的图片特殊处理
                    width: 120,
                    align: 'center'
                }
            ];
        },

        // 表单字段配置（新增/编辑弹窗用）
        institutionFormFields() {
            return [
                {
                    prop: 'name',
                    label: '机构名称',
                    type: 'input',
                    required: true,
                    props: {
                        placeholder: '请输入机构名称'
                    }
                },
                {
                    prop: 'type',
                    label: '机构类型',
                    type: 'select', // 下拉选择类型
                    required: true,
                    options: [
                        { label: '企业', value: 1 },
                        { label: '高校', value: 2 }
                    ],
                    props: {
                        placeholder: '请选择机构类型'
                    }
                },
                {
                    prop: 'address',
                    label: '地址',
                    type: 'input',
                    required: true,
                    props: {
                        placeholder: '请输入机构地址'
                    }
                },
                {
                    prop: 'phone',
                    label: '联系电话',
                    type: 'input',
                    required: true,
                    props: {
                        placeholder: '请输入联系电话'
                    }
                },
                {
                    prop: 'introduction',
                    label: '机构介绍',
                    type: 'input',
                    required: true,
                    props: {
                        type: 'textarea',  // 多行文本框
                        rows: 5,
                        placeholder: '请输入机构介绍'
                    }
                },
                {
                    prop: 'introImage',
                    label: '介绍图片',
                    type: 'image-upload',
                    uploadUrl: baseData.IMG_BASE_URL,
                    required: false,  // 图片非必填
                    limit: 1, // 限制仅能上传1张图片
                    hint: '支持 JPG、PNG、WEBP 格式，大小不超过 2MB，仅可上传1张',
                    props: {
                        // 可额外配置上传参数
                    }
                }
            ];
        },
        // 表单验证规则
        institutionFormRules() {
            return {
                name: [
                    { required: true, message: '请输入机构名称', trigger: 'blur' }
                ],
                type: [
                    { required: true, message: '请选择机构类型', trigger: 'change' }
                ],
                address: [
                    { required: true, message: '请输入地址', trigger: 'blur' }
                ],
                phone: [
                    { required: true, message: '请输入联系电话', trigger: 'blur' },
                ],
                introduction: [
                    { required: true, message: '请输入机构介绍', trigger: 'blur' }
                ]
            };
        }
    },
    mounted() {
        this.loadInstitutionList(this.baseUrl)
    },
    methods: {
        // ---------------------- 数据接口相关 ----------------------
        // 加载机构列表数据
        async loadInstitutionList(url) {
            try {
                const response = await this.$http.get(url);
                this.pagination.total = response.data.total;
                this.tableData = response.data.records;
                // 处理图片URL（复用现有工具方法）
                sliceImgUrl(this.tableData,'introImage')
                this.error = null;
            } catch (err) {
                this.error = '数据加载失败，请稍后重试';
                console.error('加载机构数据出错:', err);
            }
        },

        // ---------------------- 通用表格回调函数 ----------------------
        // 搜索回调（关键词搜索）
        handleSearch(keyword) {
            console.log('搜索关键词：', keyword);
            this.keyword = keyword
            this.pagination.currentPage = 1;
            this.loadInstitutionList(this.baseUrl)
        },

        // 新增数据回调
        async handleCreate(formData) {
            console.log('新增机构数据：', formData);
            try {
                const res = await institutionApi.create(formData);
                if (res.code == 200) {
                    this.$message.success(res.msg || '新增成功');
                    // 重新加载列表数据
                    this.loadInstitutionList(this.baseUrl);
                }
            } catch (error) {
                this.$message.error('新增失败：' + (error.msg || '网络错误'));
            }
        },

        // 编辑数据回调
        async handleUpdate(formData) {
            try {
                // 确保携带主键 id（后端需通过主键更新）
                if (!formData.id) {
                    this.$message.error('机构ID不能为空');
                    return;
                }
                console.log(institutionApi)
                const res = await institutionApi.update(formData);
                if (res.code === 200) {
                    this.$message.success(res.msg);
                    this.loadInstitutionList(this.baseUrl);
                }
            } catch (error) {
                this.$message.error('编辑失败：' + (error.msg || '网络错误'));
            }
        },

        // 删除数据回调
        async handleDelete(row) {
            console.log('删除机构数据：', row.id);
            try {
                const res = await institutionApi.delete(row.id);
                if (res.code === 200) {
                    this.$message.success(res.msg);
                    this.loadInstitutionList(this.baseUrl);
                }
            } catch (err) {
                this.$message.error('删除失败' + (err.msg || '网络错误'))
            }
        },

        // 分页变化回调
        handlePaginationChange(pagination) {
            console.log('分页变化：', pagination);
            this.pagination = pagination;
            if (this.keyword !== '') {
                this.loadInstitutionList(this.keywordUrl(this.keyword));
            } else {
                this.loadInstitutionList(this.baseUrl);
            }
        },

        // 选择行变化回调
        handleSelectionChange(selectedRows) {
            console.log('选中的机构：', selectedRows);
            // 可用于批量操作（如批量删除、批量导出等）
        }
    },
};
</script>

<style scoped>
.institution-management {
    padding: 20px;
    background-color: #f5f7fa;
    min-height: 100vh;
}
</style>